import React, { useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from 'reactstrap';
import { Collapse } from 'site-modules/shared/components/collapse/collapse';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { AnswerItems } from 'site-modules/personalized-search/components/extract/answer-items';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { EXTRACT_CREATIVE_ID } from 'site-modules/shared/constants/personalized-search/personalized-search';

import './extract-item.scss';

export function ExtractItem({ answer, question, isInitiallyExpanded }) {
  const [isOpen, toggleIsOpen] = useToggle(isInitiallyExpanded);
  const [answersExpanded, toggleAnswers] = useToggle(false);
  const answerBtnId = useId();
  const answersCollapseId = useId();

  const handleToggleAnswers = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: TrackingConstant.ACTION_VIEW_CONTENT,
        creative_id: EXTRACT_CREATIVE_ID,
        value: answersExpanded ? 'Show less' : 'Show more',
      },
    });
    toggleAnswers();
  }, [answersExpanded, toggleAnswers]);

  return (
    <li className="extract-item mb-1 mb-md-0_75">
      <div className="question d-flex align-items-center justify-content-between">
        {question}
        <Button
          color="link"
          className="answer-btn bg-white fw-normal border-0 p-0 ms-0_5 d-flex align-items-center justify-content-center"
          onClick={toggleIsOpen}
          aria-expanded={isOpen}
          aria-controls={answerBtnId}
          aria-label={isOpen ? 'Close answer' : 'Show answer'}
          data-tracking-id="view_content"
        >
          <i
            className={classnames('medium text-cool-gray-40', {
              'icon-arrow-up4': isOpen,
              'icon-arrow-down4': !isOpen,
            })}
            aria-hidden
          />
        </Button>
      </div>
      <Collapse
        id={answerBtnId}
        isOpen={isOpen}
        key={`${question}-${answersExpanded}`}
        hideCollapsedInteractiveElements={false}
      >
        {answer.length > 5 ? (
          <>
            <AnswerItems
              answerItems={answer.slice(0, 5)}
              classNames={classnames({ 'list-unstyled': answer.length === 1 })}
            />
            <Collapse
              id={answersCollapseId}
              isOpen={answersExpanded}
              addState
              transition="height 280ms cubic-bezier(0.4, 0, 0.2, 1)"
            >
              <AnswerItems
                answerItems={answer.slice(5)}
                classNames={classnames({ 'list-unstyled': answer.length === 1 })}
              />
            </Collapse>
            <Button
              onClick={handleToggleAnswers}
              color="link"
              className="answers-toggle-btn d-flex align-items-center text-transform-none medium p-0 fw-normal mt-0_5 text-primary-darker"
              aria-expanded={answersExpanded}
              aria-controls={answersCollapseId}
            >
              {answersExpanded ? 'Show less' : 'Show more'}
              <i
                className={classnames('ms-0_5 size-10', {
                  'icon-arrow-down4': !answersExpanded,
                  'icon-arrow-up4': answersExpanded,
                })}
                aria-hidden
              />
            </Button>
          </>
        ) : (
          <AnswerItems answerItems={answer} classNames={classnames({ 'list-unstyled': answer.length === 1 })} />
        )}
      </Collapse>
      <hr className="my-1 my-md-0_75" />
    </li>
  );
}

ExtractItem.propTypes = {
  answer: PropTypes.arrayOf(
    PropTypes.shape({
      answer: PropTypes.string,
      sources: PropTypes.shape({}),
    })
  ).isRequired,
  question: PropTypes.string.isRequired,
  isInitiallyExpanded: PropTypes.bool.isRequired,
};
